import * as React from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
}

const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  ({ options, value, onChange, placeholder, label, error, className, disabled = false }, ref) => {
    const [open, setOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);
    const [focusIdx, setFocusIdx] = React.useState(-1);
    const selectId = React.useId();

    // Merge forwarded ref with internal ref
    React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    const selected = options.find((o) => o.value === value);

    React.useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    React.useEffect(() => {
      if (open) {
        const idx = options.findIndex((o) => o.value === value);
        setFocusIdx(idx >= 0 ? idx : 0);
      }
    }, [open, options, value]);

    React.useEffect(() => {
      if (open && listRef.current && focusIdx >= 0) {
        const el = listRef.current.children[focusIdx] as HTMLElement | undefined;
        el?.scrollIntoView({ block: 'nearest' });
      }
    }, [focusIdx, open]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;

      if (!open) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusIdx((i) => Math.min(i + 1, options.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusIdx((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusIdx >= 0 && focusIdx < options.length) {
            onChange(options[focusIdx].value);
            setOpen(false);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
      }
    };

    return (
      <div ref={containerRef} className={cn('relative', className)}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-xs font-medium text-raios-text-secondary mb-1.5"
          >
            {label}
          </label>
        )}
        <button
          id={selectId}
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            'w-full flex items-center justify-between px-3 py-2.5 bg-raios-card border rounded-md text-sm text-left transition-colors min-h-[44px] sm:min-h-0',
            'focus:outline-none focus:ring-2 focus:ring-raios-accent/40',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            open
              ? 'border-raios-accent ring-2 ring-raios-accent/40'
              : 'border-raios-border hover:border-raios-elevated',
            error && 'border-raios-danger focus:ring-raios-danger/40'
          )}
        >
          {/* truncate + min-w-0: label longo (nomes de modelo) estourava a box
              no grid compacto dos cards de agente (correção 2026-07-19). */}
          <span
            className={cn(
              'min-w-0 flex-1 truncate',
              selected ? 'text-raios-text' : 'text-raios-muted',
            )}
          >
            {selected ? selected.label : placeholder ?? 'Selecione...'}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-raios-muted transition-transform shrink-0',
              open && 'rotate-180'
            )}
          />
        </button>

        {open && (
          <div
            ref={listRef}
            className="absolute z-100 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-raios-border bg-raios-elevated shadow-raios-lg"
            role="listbox"
          >
            {options.map((opt, i) => (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={opt.value === value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-2.5 text-sm transition-colors flex items-center gap-2',
                  i === focusIdx
                    ? 'bg-raios-accent/15 text-raios-accent-text'
                    : opt.value === value
                      ? 'bg-raios-accent/10 text-raios-text'
                      : 'text-raios-text-secondary hover:bg-raios-card hover:text-raios-text',
                  i === 0 && 'rounded-t-lg',
                  i === options.length - 1 && 'rounded-b-lg'
                )}
              >
                <div className="flex-1 min-w-0">
                  <span className="block font-medium truncate">{opt.label}</span>
                  {opt.description && (
                    <span className="block text-xs text-raios-muted mt-0.5 truncate">
                      {opt.description}
                    </span>
                  )}
                </div>
                {opt.value === value && (
                  <Check className="h-4 w-4 text-raios-accent shrink-0" />
                )}
              </button>
            ))}
          </div>
        )}

        {error && (
          <p className="mt-1.5 text-xs text-raios-danger-text">{error}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
export type { SelectProps, SelectOption };
